import { toError, useInitiateDepositMutation } from '@massilia/queries'
import { Button, Input, Label } from '@massilia/ui'
import { CreditCard, Info, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface DepositCardFormProps {
  credentials: { dossierNumber: string; email: string; lastName: string }
  /** Montant minimum imposé au tout premier acompte, `null` si non applicable. */
  minimumFirstDepositCents: null | number
  /** Solde restant du dossier, en centimes : plafond du montant saisissable. */
  remainingAmount: number
}

/**
 * Versement d'un acompte par carte bancaire : le client choisit librement son montant,
 * dans la limite du solde restant.
 */
export function DepositCardForm({
  credentials,
  minimumFirstDepositCents,
  remainingAmount,
}: DepositCardFormProps) {
  const { isPending, mutateAsync: initiateDeposit } = useInitiateDepositMutation()
  const [amountEuros, setAmountEuros] = useState('')
  const [submitError, setSubmitError] = useState<null | string>(null)

  const maxAmountEuros = centsToEuros(remainingAmount)
  const minAmountEuros = minimumFirstDepositCents ? centsToEuros(minimumFirstDepositCents) : 0.01

  const handleSubmit = async () => {
    setSubmitError(null)

    const parsedAmount = Number.parseFloat(amountEuros.replace(',', '.'))
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setSubmitError('Veuillez saisir un montant valide.')
      return
    }

    const amountCents = Math.round(parsedAmount * 100)

    if (minimumFirstDepositCents && amountCents < minimumFirstDepositCents) {
      setSubmitError(
        `Le montant minimum du premier acompte est de ${formatPrice(centsToEuros(minimumFirstDepositCents))}.`
      )
      return
    }

    if (amountCents > remainingAmount) {
      setSubmitError(
        `Le montant ne peut pas dépasser le solde restant (${formatPrice(maxAmountEuros)}).`
      )
      return
    }

    try {
      const result = await initiateDeposit({
        ...credentials,
        amount: amountCents,
      })
      window.location.assign(result.axeptaRedirectUrl)
    } catch (error) {
      setSubmitError(resolveAuthErrorMessage(toError(error)))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {minimumFirstDepositCents && (
        <div className="flex items-center gap-3 rounded-lg bg-amber-50 p-4">
          <Info className="size-5 shrink-0 text-amber-600" />
          <p className="text-sm font-medium text-amber-800">
            Il s&apos;agit de votre premier acompte. Le montant minimum est de{' '}
            {formatPrice(centsToEuros(minimumFirstDepositCents))}.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="deposit-amount">Montant (en euros)</Label>
        <div className="relative">
          <Input
            aria-describedby={submitError ? 'deposit-amount-error' : undefined}
            aria-invalid={submitError !== null}
            id="deposit-amount"
            inputMode="decimal"
            max={maxAmountEuros}
            min={minAmountEuros}
            onChange={(e) => {
              setAmountEuros(e.target.value)
              if (submitError) setSubmitError(null)
            }}
            placeholder={`Max. ${formatPrice(maxAmountEuros)}`}
            step={0.01}
            type="number"
            value={amountEuros}
          />
          <span className="text-gray-light-400 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
            EUR
          </span>
        </div>
      </div>

      {submitError && (
        <p className="text-sm font-medium text-red-600" id="deposit-amount-error" role="alert">
          {submitError}
        </p>
      )}

      <Button
        className="w-full bg-brand-600 px-4 py-3 text-base font-medium text-white hover:bg-brand-700"
        disabled={isPending || !amountEuros}
        onClick={() => void handleSubmit()}
        type="button"
      >
        {isPending ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <>
            <CreditCard className="mr-2 size-4" />
            Payer un acompte en CB
          </>
        )}
      </Button>

      <p className="text-center text-xs text-gray-light-500">
        Vous serez redirigé vers notre plateforme de paiement sécurisée.
      </p>
    </div>
  )
}
